'use client';

import React, { useState, useRef, useEffect } from 'react';
import { TimelineDropdownItem } from './navigation-bar-horizontal';
import { ThreatReportKey, WorkProduct, WorkProductType } from '@/shared/types';
import {
  CalendarBlankIcon,
  UserIcon,
  UsersIcon,
  ListBulletsIcon,
  ListChecksIcon,
  SpinnerGapIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import { recordStat, formatTimelineDisplayText } from '@/shared/functions';
import type { ThreatReportSummary } from '@/shared/cors-edge-lambda-api';
import { useThreatReportCreators } from '@/hooks/useThreatReportCreators';
import { useCaseThreatReports } from '@/hooks/useCaseThreatReports';
import { ThreatReportShareModal } from '@/components/threat-report-share-modal';
import { Button } from '@tranquility-ai/theme-components/button';
import { Menu, MenuItem } from '@tranquility-ai/theme-components/menu';
import { StatusPill } from '@tranquility-ai/theme-components/status-pill';
import { WorkProductCard } from '@tranquility-ai/theme-components/work-product-card';
import {
  selectorCountLabel,
  threatReportSelectorCount,
} from '@/shared/threat-report-card-meta';
import { displayThreatReportTitle, summaryToThreatReportKey, threatReportGenerationDescription } from '@/components/threat-report-version-header';
import { useAppFormatters } from '@/i18n/useAppFormatters';
import { useGridLocaleText } from '@/i18n/useGridLocaleText';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { StatsContext } from '@/app/contexts';
import { useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ShareNetworkIcon } from '@phosphor-icons/react';
import { workProductGridTheme } from '@/components/work-product-grid-theme';

export interface WorkProductIndexProps {
  caseId: string | undefined;
  timelines: TimelineDropdownItem[];
  onCreateTimeline: () => void;
  onSelectTimeline: (timelineId: string) => void;
  onSelectThreatReport?: (key: ThreatReportKey) => void;
  featureFlags?: { [key: string]: any };
  isLoading?: boolean;
  viewMode?: 'grid' | 'list';
  isThreatReportProcessing?: boolean;
  processingThreatReportId?: string | null;
  clearThreatReportKey: () => void;
  /** S3 upload time keyed by export URL, for reports whose filename has no date. */
  sourceModifiedAt?: Record<string, string>;
}

function ThreatReportSourceMeta({
  caseId,
  creatorIds,
  source,
  appearance = 'card',
}: {
  caseId: string | undefined;
  creatorIds: string[];
  source: string | null | undefined;
  /** Card footer includes the icon. The list Count column is text only. */
  appearance?: 'card' | 'list';
}) {
  const { data: creators } = useThreatReportCreators(caseId, source || undefined);
  const count = threatReportSelectorCount(creatorIds, creators);
  if (count === null) {
    if (appearance === 'list') return <span className="text-white/30 text-xs">—</span>;
    return null;
  }
  const label = selectorCountLabel(count);

  if (appearance === 'list') {
    return <span className="text-white/65 text-xs">{label}</span>;
  }

  return (
    <div className="flex flex-row items-center gap-1">
      <ListChecksIcon size={14} weight="bold" />
      <span>{label}</span>
    </div>
  );
}

const WorkProductIndex: React.FC<WorkProductIndexProps> = ({
  caseId,
  timelines,
  onCreateTimeline,
  onSelectTimeline,
  onSelectThreatReport,
  featureFlags: _featureFlags,
  isLoading = false,
  viewMode = 'grid',
  isThreatReportProcessing = false,
  processingThreatReportId = null,
  clearThreatReportKey,
  sourceModifiedAt = {},
}) => {
  const [workProducts, setWorkProducts] = useState<WorkProduct[]>([]);
  const [shareReport, setShareReport] = useState<ThreatReportSummary | null>(null);
  const [actionsMenuId, setActionsMenuId] = useState<string | null>(null);
  const reportsByIdRef = useRef<Map<string, ThreatReportSummary>>(new Map());
  const t = useTimePilotT();
  const { formatDate: formatLocaleDate } = useAppFormatters();
  const gridLocaleText = useGridLocaleText();

  const statsContext = useContext(StatsContext);
  const gridRef = useRef<AgGridReact>(null);
  const { reports: threatReports } = useCaseThreatReports(caseId);

  useEffect(() => {
    clearThreatReportKey();
  }, [caseId]);

  // Convert timelines to work products
  useEffect(() => {
    const timelineProducts: WorkProduct[] = timelines.map((timeline) => ({
      id: timeline.id,
      type: (timeline.id.startsWith('threat-report-') ? 'threat-report' : 'timeline') as WorkProductType,
      title: formatTimelineDisplayText(timeline.title, t('nav.allCases')) || t('workProducts.untitled'),
      description: formatTimelineDisplayText(timeline.description, t('nav.allCases')),
      isRead: false, // TODO: Implement read state tracking
      verificationState: 'unverified' as const, // TODO: Implement verification state
      createdAt: timeline.createdAt,
      createdBy: timeline.createdBy,
      resourceId: timeline.id,
      eventCount: timeline.eventCount,
    }));

    const reportProducts: WorkProduct[] = threatReports.map((report) => {
      const id = `threat-report:${report.chatKey}:${report.epochTimeSecs}`;
      const description = threatReportGenerationDescription(report, sourceModifiedAt, t);
      return {
        id,
        type: 'threat-report' as WorkProductType,
        title: displayThreatReportTitle(report.title, t),
        status: report.status,
        description,
        isRead: false,
        verificationState: 'unverified' as const,
        resourceId: id,
        createdAt: report.timestamp ?? new Date(report.epochTimeSecs * 1000).toISOString(),
        createdBy: report.createdBy ?? undefined,
        creatorIds: report.creatorIds,
        fivecastDataDirectory: report.fivecastDataDirectory,
        shared: report.shared,
        isOwner: report.isOwner,
      };
    });
    reportsByIdRef.current = new Map(
      threatReports.map((report) => [`threat-report:${report.chatKey}:${report.epochTimeSecs}`, report]),
    );
    setWorkProducts([...timelineProducts, ...reportProducts]);
  }, [timelines, caseId, threatReports, sourceModifiedAt, t]);

  const handleWorkProductClick = (product: WorkProduct) => {
    if (product.type === 'timeline' && product.resourceId) {
      onSelectTimeline(product.resourceId);
      recordStat(
        'TimelineButtonClicked',
        new MouseEvent('click') as any,
        statsContext      );
    } else if (product.type === 'threat-report') {
      const report = reportsByIdRef.current.get(product.id);
      if (onSelectThreatReport && report) {
        onSelectThreatReport(summaryToThreatReportKey(report));
        recordStat(
          'ThreatReportButtonClicked',
          new MouseEvent('click') as any,
          statsContext        );
      }
    }
  };

  const getTypeLabel = (type: WorkProductType) => {
    switch (type) {
      case 'timeline':
        return t('workProducts.timeline');
      case 'threat-report':
        return t('workProducts.threatReport');
      default:
        return t('workProducts.type.document');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return formatLocaleDate(date, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const getCreatedByShort = (createdBy?: string) => {
    if (!createdBy) return '';
    return createdBy.split('@')[0];
  };

  // Column definitions for ag-grid list view
  const columnDefs = [
    {
      field: 'title',
      headerName: t('timeline.table.column.title'),
      flex: 2.4,
      minWidth: 260,
      cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden', minWidth: 0 },
      cellRenderer: (params: any) => {
        const product = params.data as WorkProduct;
        const title = formatTimelineDisplayText(product.title, t('nav.allCases'));
        return (
          <span className="flex w-full min-w-0 items-center gap-2 overflow-hidden">
            <span
              className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-white"
              title={title || undefined}
            >
              {title}
            </span>
            {product.shared ? (
              <StatusPill color="brand" inverse size="sm" className="shrink-0">
                {t('workProducts.shared')}
              </StatusPill>
            ) : null}
          </span>
        );
      },
    },
    {
      field: 'description',
      headerName: t('timeline.table.column.description'),
      flex: 3.6,
      minWidth: 360,
      cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden', minWidth: 0 },
      cellRenderer: (params: any) => {
        const product = params.data as WorkProduct;
        const description = formatTimelineDisplayText(product.description || '', t('nav.allCases'));
        if (!description) {
          return <span className="text-sm text-white/65">—</span>;
        }
        return (
          <span
            className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-white/65"
            title={description}
          >
            {description}
          </span>
        );
      },
    },
    {
      field: 'type',
      headerName: t('workProducts.column.type'),
      flex: 1,
      minWidth: 120,
      maxWidth: 160,
      cellRenderer: (params: any) => {
        const product = params.data as WorkProduct;
        return (
          <span className="text-sm font-medium text-white">{getTypeLabel(product.type)}</span>
        );
      },
    },
    {
      field: 'eventCount',
      headerName: t('workProducts.column.count'),
      flex: 0.9,
      minWidth: 108,
      maxWidth: 140,
      cellRenderer: (params: any) => {
        const product = params.data as WorkProduct;
        if (product.type === 'timeline' && product.eventCount !== undefined) {
          return (
            <span className="text-white/65 text-xs">{t(product.eventCount === 1 ? 'workProducts.eventCountOne' : 'workProducts.eventCountMany', { count: product.eventCount })}</span>
          );
        }
        if (product.type === 'threat-report') {
          return (
            <ThreatReportSourceMeta
              caseId={caseId}
              creatorIds={product.creatorIds ?? []}
              source={product.fivecastDataDirectory}
              appearance="list"
            />
          );
        }
        return <span className="text-white/30 text-xs">—</span>;
      },
    },
    {
      field: 'createdBy',
      headerName: t('workProducts.column.creator'),
      flex: 1.2,
      minWidth: 150,
      maxWidth: 200,
      cellRenderer: (params: any) => {
        const product = params.data as WorkProduct;
        if (!product.createdBy && !product.shared) {
          return <span className="text-white/30 text-xs">—</span>;
        }
        const creator = getCreatedByShort(product.createdBy);
        return (
          <span className="flex min-w-0 items-center gap-1 text-xs">
            {product.shared ? (
              <UsersIcon size={14} weight="bold" className="shrink-0 text-[var(--text-brand)]" aria-hidden />
            ) : (
              <UserIcon size={14} weight="bold" className="shrink-0 text-white/65" aria-hidden />
            )}
            <span className={product.shared ? 'truncate text-[var(--text-brand)]' : 'truncate text-white/65'}>
              {creator || '—'}
            </span>
          </span>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: t('workProducts.column.created'),
      width: 112,
      headerClass: 'ag-right-aligned-header',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
      cellRenderer: (params: any) => {
        const product = params.data as WorkProduct;
        // Check if this threat report is currently processing
        const isProcessing =
          product.type === 'threat-report' &&
          (product.status === 'inprogress' ||
            (isThreatReportProcessing && product.id === processingThreatReportId));

        if (isProcessing) {
          return (
            <StatusPill color="neutral" inverse size="sm" leadingIcon={<SpinnerGapIcon className="animate-spin" weight="bold" aria-hidden />} role="status">
              {t('workProducts.generating')}
            </StatusPill>
          );
        }

        if (product.createdAt) {
          return (
            <span className="text-white/65 text-xs">{formatDate(product.createdAt)}</span>
          );
        } else if (product.type === 'threat-report') {
          return (
            <span className="text-white/30 text-xs">—</span>
          );
        }
        return <span className="text-white/30 text-xs">—</span>;
      },
    },
    {
      colId: 'actions',
      headerName: '',
      width: 44,
      sortable: false,
      resizable: false,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
      cellRenderer: (params: any) => {
        const product = params.data as WorkProduct;
        const report = reportsByIdRef.current.get(product.id);
        if (product.type !== 'threat-report' || !product.isOwner || product.status !== 'completed' || !report) {
          return null;
        }
        const open = actionsMenuId === product.id;
        return (
          <div data-work-product-actions="" onClick={(event) => event.stopPropagation()}>
            <Menu
              isOpen={open}
              onOpenChange={(next) => setActionsMenuId(next ? product.id : null)}
              inverted
              align="end"
              aria-label={`More actions for ${product.title}`}
              content={
                <MenuItem icon={<ShareNetworkIcon />} onSelect={() => setShareReport(report)}>
                  {product.shared ? 'Manage sharing' : 'Share'}
                </MenuItem>
              }
            >
              <Button
                iconOnly
                size="sm"
                style="text"
                color="light"
                background="inverse"
                aria-label={`More actions for ${product.title}`}
                leadingIcon={<DotsThreeVerticalIcon weight="bold" />}
                onClick={() => setActionsMenuId(open ? null : product.id)}
              />
            </Menu>
          </div>
        );
      },
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: false,
    cellStyle: { display: 'flex', alignItems: 'center' },
  }

  const onRowClicked = (params: any) => {
    const target = params.event?.target as HTMLElement | null;
    if (target?.closest('[data-work-product-actions]')) return;
    const product = params.data as WorkProduct;
    handleWorkProductClick(product);
  };

  return (
    <div className="w-full h-full bg-[#0F1D32] p-[30px] mt-[30px] sm:mt-0 box-border">
      {isLoading ? (
        <div
          className="flex flex-col items-center justify-center min-h-[400px]"
          aria-live="polite"
          aria-busy="true"
          role="status"
        >
          <SpinnerGapIcon size={48} className="text-white animate-spin mb-4" aria-hidden="true" />
          <p className="text-white/70 text-lg">{t('workProducts.loading')}</p>
        </div>
      ) : workProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-white/5 rounded-lg border border-white/10">
          <p className="text-white/70 text-lg mb-6">{t('workProducts.empty')}</p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="w-full h-full ag-theme-quartz" role="region" aria-label={t('workProducts.table')}>
          <AgGridReact
            ref={gridRef}
            rowData={workProducts}
            columnDefs={columnDefs as any}
            localeText={gridLocaleText}
            defaultColDef={defaultColDef as any}
            onRowClicked={onRowClicked}
            gridOptions={{
              suppressScrollOnNewData: true,
              rowStyle: {
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              },
              getRowHeight: () => 60,
            }}
            theme={workProductGridTheme}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-full">
          {workProducts.map((product) => {
            const report = reportsByIdRef.current.get(product.id);
            const displayTitle = formatTimelineDisplayText(product.title, t('nav.allCases'));
            const displayDescription = formatTimelineDisplayText(product.description || '', t('nav.allCases'));
            const isProcessing =
              product.type === 'threat-report' &&
              (product.status === 'inprogress' ||
                (isThreatReportProcessing && product.id === processingThreatReportId));
            const creator = getCreatedByShort(product.createdBy);
            const meta = [];
            if (product.type === 'timeline' && product.eventCount !== undefined) {
              meta.push({
                id: 'events',
                icon: <ListBulletsIcon weight="bold" />,
                label: t(product.eventCount === 1 ? 'workProducts.eventCountOne' : 'workProducts.eventCountMany', { count: product.eventCount }),
              });
            }
            if (product.type === 'threat-report') {
              meta.push({
                id: 'selectors',
                label: (
                  <ThreatReportSourceMeta
                    caseId={caseId}
                    creatorIds={product.creatorIds ?? []}
                    source={product.fivecastDataDirectory}
                  />
                ),
              });
            }
            if (creator) {
              meta.push({
                id: 'creator',
                icon: product.shared ? <UsersIcon weight="bold" /> : <UserIcon weight="bold" />,
                label: creator,
                title: product.shared
                  ? product.isOwner
                    ? 'Shared with your organization'
                    : `Shared by ${creator}`
                  : product.createdBy,
                highlight: product.shared,
              });
            }
            const canShare = Boolean(
              product.type === 'threat-report' && product.isOwner && product.status === 'completed' && report,
            );
            return (
              <WorkProductCard
                key={product.id}
                tone="inverse"
                type={product.type === 'timeline' ? 'timeline' : 'threat-report'}
                title={displayTitle}
                description={displayDescription || undefined}
                status={isProcessing ? 'processing' : product.status === 'error' ? 'error' : 'ready'}
                meta={meta}
                date={
                  product.createdAt ? (
                    <>
                      <CalendarBlankIcon weight="bold" />
                      {formatDate(product.createdAt)}
                    </>
                  ) : undefined
                }
                shared={product.shared}
                sharedDescription={product.isOwner ? 'Shared with your organization' : `Shared by ${creator}`}
                menu={
                  canShare && report
                    ? {
                        share: {
                          label: product.shared ? 'Manage sharing' : 'Share',
                          onSelect: () => setShareReport(report),
                        },
                      }
                    : undefined
                }
                onClick={() => handleWorkProductClick(product)}
              />
            );
          })}
        </div>
      )}
      <ThreatReportShareModal caseId={caseId} report={shareReport} onClose={() => setShareReport(null)} />
    </div>
  );
};

export default WorkProductIndex;
